import { Box, Collapse, Divider, List, Typography } from '@mui/material'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { localStorageItemKeys } from '~constants/local-storage-items'
import { useNavigation } from '../../navigation-context'
import { MenuItem } from './menu-item'
import { MenuItemLayout } from './menu-item-layout'
import { MenuItemTooltip } from './menu-item-tooltip'
import { ChevronDownIcon, ChevronUpIcon } from '~components/icons'

interface NestedMenuItemProps {
  parent: string
  menuItemChildren: Array<{ label: string; link: string }>
  icon: ReactNode
  tooltipTitle?: string
  className?: string
}

export function NestedMenuItemWithIcon({
  parent,
  menuItemChildren,
  icon,
  tooltipTitle,
  className,
}: NestedMenuItemProps) {
  const { isOpen: isMenuOpen, tourLink } = useNavigation()
  const [isToolTipOpen, setIsToolTipOpen] = useState(false)
  const [isAccordionOpen, setIsAccordionOpen] = useState(
    getDefaultAccordionState(parent),
  )
  const location = useLocation()

  useEffect(() => {
    setIsToolTipOpen(false)
  }, [location.pathname])

  const isAnyChildActive = useMemo(
    () =>
      menuItemChildren.some((child) => {
        const normalizedPathname = decodeURIComponent(location.pathname)
        const normalizedChildLink = decodeURIComponent(child.link.split('?')[0])
        return normalizedPathname.startsWith(normalizedChildLink)
      }),
    [menuItemChildren, location.pathname],
  )

  const toggleAccordion = () => {
    if (!isMenuOpen) return

    setIsAccordionOpen((open) => !open)
    const accordionStateRaw = localStorage.getItem(
      localStorageItemKeys.navigationAccordionState,
    )
    const accordionState = accordionStateRaw
      ? JSON.parse(accordionStateRaw)
      : {}
    accordionState[parent] = !isAccordionOpen
    localStorage.setItem(
      localStorageItemKeys.navigationAccordionState,
      JSON.stringify(accordionState),
    )
  }

  return (
    <>
      <MenuItemTooltip
        open={isToolTipOpen}
        onOpen={() => setIsToolTipOpen(true)}
        onClose={() => setIsToolTipOpen(false)}
        disableFocusListener
        title={
          <NestedItemTooltipContent
            childItems={menuItemChildren}
            parentTitle={tooltipTitle ?? parent}
          />
        }
      >
        <MenuItemLayout
          forceHoverState={isToolTipOpen}
          icon={icon}
          label={parent}
          isActive={isAnyChildActive}
          className={className}
          suffixElement={
            <Box sx={{ display: isMenuOpen ? 'block' : 'none' }}>
              {isAccordionOpen ? (
                <ChevronUpIcon width="20px" height="20px" />
              ) : (
                <ChevronDownIcon width="20px" height="20px" />
              )}
            </Box>
          }
          onClick={toggleAccordion}
        />
      </MenuItemTooltip>
      <Collapse
        in={
          isAccordionOpen ||
          menuItemChildren.some((child) => child.link === tourLink)
        }
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
          sx={{ display: isMenuOpen ? 'block' : 'none' }}
        >
          {menuItemChildren?.map((child) => (
            <Box sx={{ mt: 1 }} key={child.link}>
              <MenuItem link={child.link} label={child.label} />
            </Box>
          ))}
        </List>
        <Divider
          sx={{
            backgroundColor: 'white',
            display: isMenuOpen ? 'block' : 'none',
          }}
        />
      </Collapse>
    </>
  )
}

function getDefaultAccordionState(itemName: string) {
  const defaultAccordionStateRaw = localStorage.getItem(
    localStorageItemKeys.navigationAccordionState,
  )
  const defaultAccordionState = defaultAccordionStateRaw
    ? JSON.parse(defaultAccordionStateRaw)
    : {}
  const value = defaultAccordionState[itemName]
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const v = value.toLowerCase()
    if (v === 'true') return true
    if (v === 'false') return false
  }
  return false
}

function NestedItemTooltipContent({
  childItems,
  parentTitle,
}: {
  childItems: Array<{ label: string; link: string }>
  parentTitle: string
}) {
  return (
    <>
      <Typography variant="menuBold" sx={{ mx: 3 }}>
        {parentTitle}
      </Typography>
      <Divider sx={{ mt: 2, mb: 1 }} />
      <List component="div" disablePadding sx={{ display: 'block' }}>
        {childItems?.map((child) => (
          <Box
            sx={{ '& span': { p: '0 !important', m: 0 }, '& li': { m: 0 } }}
            key={child.link}
          >
            <MenuItem link={child.link} label={child.label} />
          </Box>
        ))}
      </List>
    </>
  )
}
